import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { HttpCacheKeyPrefix } from '@lib/core/cache-key-prefix';
import { HttpExtras } from '@lib/core/http-extras';
import { EnvService } from '@lib/env';
import { Observable, map } from 'rxjs';
import { CreatePurchasingGroupCommand } from '../models/create-purchasing-group-command.model';
import {
    IPurchasingGroupAssignmentConflictDto,
    PurchasingGroupAssignmentConflict,
} from '../models/purchasing-group-assignment-conflict.model';
import { PurchasingGroupAssignment } from '../models/purchasing-group-assignment.model';
import { PurchasingGroupAssignableUser } from '../models/purchasing-group-assignable-user.model';
import { PurchasingGroupCompanyCodeRemovalBlocked } from '../models/purchasing-group-company-code-removal-blocked.model';
import { PurchasingGroupDetail } from '../models/purchasing-group-detail.model';
import { PurchasingGroupResponsibilityOccupation } from '../models/purchasing-group-responsibility-occupation.model';
import { PurchasingGroup } from '../models/purchasing-group.model';
import { PurchasingGroupId } from '../models/purchasing-group-id.model';
import { PurchasingGroupMembers } from '../models/purchasing-group-members.model';
import { PurchasingGroupSettings } from '../models/purchasing-group-settings.model';
import { ResponsiblePurchasingGroup } from '../models/responsible-purchasing-group.model';
import { UpdatePurchasingGroupCommand } from '../models/update-purchasing-group-command.model';
import { UpdatePurchasingGroupSettingsCommand } from '../models/update-purchasing-group-settings-command.model';
import { IAssignPurchasingGroupAssignmentRequestDto } from '../types/assign-purchasing-group-assignment-request.dto';
import { IBulkAssignPurchasingGroupAssignmentsRequestDto } from '../types/bulk-assign-purchasing-group-assignments-request.dto';
import { IBulkRemovePurchasingGroupAssignmentsRequestDto } from '../types/bulk-remove-purchasing-group-assignments-request.dto';
import { ICreatePurchasingGroupRequestDto } from '../types/create-purchasing-group-request.dto';
import { IDeactivatePurchasingGroupRequestDto } from '../types/deactivate-purchasing-group-request.dto';
import { IPurchasingGroupAssignableCompanyCodeDto } from '../types/purchasing-group-assignable-company-code.dto';
import { IPurchasingGroupAssignmentDto } from '../types/purchasing-group-assignment.dto';
import { IPurchasingGroupAssignableUserDto } from '../types/purchasing-group-assignable-user.dto';
import { IPurchasingGroupCompanyCodeRemovalBlockedDto } from '../types/purchasing-group-company-code-removal-blocked.dto';
import { IPurchasingGroupDetailDto } from '../types/purchasing-group-detail.dto';
import { IPurchasingGroupResponsibilityOccupationDto } from '../types/purchasing-group-responsibility-occupation.dto';
import { IPurchasingGroupDto } from '../types/purchasing-group.dto';
import { IPurchasingGroupErrorDto } from '../types/purchasing-group-error.dto';
import { IPurchasingGroupMembersDto } from '../types/purchasing-group-members.dto';
import { IPurchasingGroupMembersRequestDto } from '../types/purchasing-group-members-request.dto';
import { IPurchasingGroupSettingsDto } from '../types/purchasing-group-settings.dto';
import { IRemovePurchasingGroupAssignmentRequestDto } from '../types/remove-purchasing-group-assignment-request.dto';
import { IResponsiblePurchasingGroupDto } from '../types/responsible-purchasing-group.dto';
import { IUpdatePurchasingGroupCompanyCodesRequestDto } from '../types/update-purchasing-group-company-codes-request.dto';
import { IUpdatePurchasingGroupRequestDto } from '../types/update-purchasing-group-request.dto';
import { IUpdatePurchasingGroupSettingsRequestDto } from '../types/update-purchasing-group-settings-request.dto';
import { PurchasingGroupErrorCode } from '../types/purchasing-group-error-code.enum';

@Injectable({ providedIn: 'root' })
export class PurchasingGroupDataService {
    private readonly http = inject(HttpClient);
    private readonly env = inject(EnvService);

    public getAll(): Observable<ReadonlyArray<PurchasingGroup>> {
        const url = this.env.getBase('purchasing-groups');
        return this.http
            .get<IPurchasingGroupDto[]>(url.toString(), {
                params: this.ignoreCacheParams(),
            })
            .pipe(map((response) => response.map((dto) => new PurchasingGroup(dto))));
    }

    /**
     * Lean, non-admin variant of {@link getAll} for accounting selection dropdowns. Hits the
     * SCOPE_customer-only /purchasing-groups/selectable endpoint (no purchasing_group/show
     * permission required), so a demand approver can populate the purchasing-group dropdown without
     * triggering a 403. The response carries only id/code/name/active; admin-only fields stay empty.
     */
    public getSelectable(): Observable<ReadonlyArray<PurchasingGroup>> {
        const url = this.env.getBase('purchasing-groups', 'selectable');
        return this.http
            .get<IPurchasingGroupDto[]>(url.toString(), {
                params: this.ignoreCacheParams(),
            })
            .pipe(map((response) => response.map((dto) => new PurchasingGroup(dto))));
    }

    /**
     * EPROC-11622: resolves which purchasing group is responsible for a company code and commodity
     * group combination, so a dialog can preview the derivation before anything is applied. Needs only
     * SCOPE_customer like {@link getSelectable}. Answers "nothing assigned" as a regular result, so the
     * caller renders an empty preview instead of an error.
     */
    public getResponsible(companyCodeId: string, materialGroupCode: string): Observable<ResponsiblePurchasingGroup> {
        const url = this.env.getBase('purchasing-groups', 'responsible');
        url.query.companyCodeId = companyCodeId;
        url.query.materialGroupCode = materialGroupCode;

        return this.http
            .get<IResponsiblePurchasingGroupDto>(url.toString(), {
                params: this.ignoreCacheParams(),
            })
            .pipe(map((response) => new ResponsiblePurchasingGroup(response)));
    }

    public getById(id: string): Observable<PurchasingGroupDetail> {
        const url = this.env.getBase('purchasing-groups', id);
        return this.http
            .get<IPurchasingGroupDetailDto>(url.toString(), {
                params: this.ignoreCacheParams(),
            })
            .pipe(map((response) => new PurchasingGroupDetail(response)));
    }

    public getSettings(): Observable<PurchasingGroupSettings> {
        const url = this.env.getBase('purchasing-groups', 'settings');
        return this.http
            .get<IPurchasingGroupSettingsDto>(url.toString(), {
                params: this.ignoreCacheParams(),
            })
            .pipe(map((response) => new PurchasingGroupSettings(response)));
    }

    public create(command: CreatePurchasingGroupCommand): Observable<PurchasingGroupId> {
        const url = this.env.getBase('purchasing-groups');
        const request: ICreatePurchasingGroupRequestDto = {
            code: command.code,
            name: command.name,
            description: command.description,
            companyCodeIds: command.companyCodeIds,
        };

        return this.http
            .post<string>(url.toString(), request, {
                params: this.pruneCacheParams(HttpCacheKeyPrefix.PURCHASING_GROUP),
            })
            .pipe(map((id) => new PurchasingGroupId(id)));
    }

    public getAssignableCompanyCodes(id: string): Observable<readonly IPurchasingGroupAssignableCompanyCodeDto[]> {
        const url = this.env.getBase('purchasing-groups', id, 'assignable-company-codes');

        return this.http.get<IPurchasingGroupAssignableCompanyCodeDto[]>(url.toString(), {
            params: this.ignoreCacheParams(),
        });
    }

    public updateCompanyCodes(id: string, companyCodeIds: readonly string[]): Observable<void> {
        const url = this.env.getBase('purchasing-groups', id, 'company-codes');
        const request: IUpdatePurchasingGroupCompanyCodesRequestDto = {
            companyCodeIds,
        };

        return this.http.put<void>(url.toString(), request, {
            params: this.pruneCacheParams(HttpCacheKeyPrefix.PURCHASING_GROUP),
        });
    }

    public update(command: UpdatePurchasingGroupCommand): Observable<void> {
        const url = this.env.getBase('purchasing-groups', command.id);
        const request: IUpdatePurchasingGroupRequestDto = {
            name: command.name,
            description: command.description,
        };

        return this.http.put<void>(url.toString(), request, {
            params: this.pruneCacheParams(HttpCacheKeyPrefix.PURCHASING_GROUP),
        });
    }

    public updateSettings(command: UpdatePurchasingGroupSettingsCommand): Observable<void> {
        const url = this.env.getBase('purchasing-groups', 'settings');
        const request: IUpdatePurchasingGroupSettingsRequestDto = {
            required: command.required,
            showDuringOrderingProcess: command.showDuringOrderingProcess,
            defaultPurchasingGroupId: command.defaultPurchasingGroupId,
        };

        return this.http.put<void>(url.toString(), request, {
            params: this.pruneCacheParams(HttpCacheKeyPrefix.PURCHASING_GROUP),
        });
    }

    public deactivate(id: string, confirmed = false): Observable<PurchasingGroupDetail> {
        const url = this.env.getBase('purchasing-groups', id, 'deactivate');
        const request: IDeactivatePurchasingGroupRequestDto = {
            confirmed,
        };

        return this.http
            .put<IPurchasingGroupDetailDto>(url.toString(), request, {
                params: this.pruneCacheParams(HttpCacheKeyPrefix.PURCHASING_GROUP),
            })
            .pipe(map((response) => new PurchasingGroupDetail(response)));
    }

    public reactivate(id: string): Observable<PurchasingGroupDetail> {
        const url = this.env.getBase('purchasing-groups', id, 'reactivate');

        return this.http
            .put<IPurchasingGroupDetailDto>(url.toString(), null, {
                params: this.pruneCacheParams(HttpCacheKeyPrefix.PURCHASING_GROUP),
            })
            .pipe(map((response) => new PurchasingGroupDetail(response)));
    }

    public getMembers(id: string): Observable<PurchasingGroupMembers> {
        const url = this.env.getBase('purchasing-groups', id, 'members');

        return this.http
            .get<IPurchasingGroupMembersDto>(url.toString(), {
                params: this.ignoreCacheParams(),
            })
            .pipe(map((response) => new PurchasingGroupMembers(response)));
    }

    public getAssignableUsers(): Observable<ReadonlyArray<PurchasingGroupAssignableUser>> {
        const url = this.env.getBase('customer');

        return this.http
            .get<IPurchasingGroupAssignableUserDto[]>(url.toString(), {
                params: this.cacheParams(HttpCacheKeyPrefix.PURCHASING_GROUP),
            })
            .pipe(map((response) => response.map((dto) => new PurchasingGroupAssignableUser(dto))));
    }

    public getAssignments(id: string): Observable<ReadonlyArray<PurchasingGroupAssignment>> {
        const url = this.env.getBase('purchasing-groups', id, 'assignments');

        return this.http
            .get<IPurchasingGroupAssignmentDto[]>(url.toString(), {
                params: this.ignoreCacheParams(),
            })
            .pipe(map((response) => response.map((dto) => new PurchasingGroupAssignment(dto))));
    }

    /**
     * @param ignoreCache forces a fresh server round-trip instead of serving the (possibly stale)
     * cached response. Needed after a save conflict: the failed mutation does not prune the cache
     * (only successful responses do), so a plain reload would replay the outdated occupation map and
     * the newly conflicting combination would stay unmarked. A fresh fetch also overwrites the stale
     * cache entry, so subsequent dialog opens are up to date again.
     */
    public getResponsibilityOccupations(
        companyCodeIds: ReadonlyArray<string>,
        ignoreCache = false,
    ): Observable<ReadonlyArray<PurchasingGroupResponsibilityOccupation>> {
        const url = this.env.getBase('purchasing-groups', 'responsibility-occupations');
        url.query.companyCodeIds = companyCodeIds.join(',');

        return this.http
            .get<IPurchasingGroupResponsibilityOccupationDto[]>(url.toString(), {
                params: new HttpExtras({
                    cacheKeyPrefix: HttpCacheKeyPrefix.PURCHASING_GROUP,
                    customErrorhandling: true,
                    ignoreCache,
                }),
            })
            .pipe(map((response) => response.map((dto) => new PurchasingGroupResponsibilityOccupation(dto))));
    }

    public assignAssignment(
        id: string,
        companyCodeId: string,
        materialGroupCode: string,
        replaceExisting = false,
    ): Observable<void> {
        const url = this.env.getBase('purchasing-groups', id, 'assignments', 'by-code');
        const request: IAssignPurchasingGroupAssignmentRequestDto = {
            companyCodeId,
            materialGroupCode,
            replaceExisting,
        };

        return this.postWithoutResponseBody(url.toString(), request, {
            customErrorhandling: true,
            pruneCacheWithPrefix: HttpCacheKeyPrefix.PURCHASING_GROUP,
        });
    }

    public removeAssignment(id: string, companyCodeId: string, materialGroupCode: string): Observable<void> {
        const url = this.env.getBase('purchasing-groups', id, 'assignments', 'by-code');
        const request: IRemovePurchasingGroupAssignmentRequestDto = {
            companyCodeId,
            materialGroupCode,
        };

        return this.deleteWithoutResponseBody(url.toString(), request, {
            customErrorhandling: true,
            pruneCacheWithPrefix: HttpCacheKeyPrefix.PURCHASING_GROUP,
        });
    }

    public bulkAssignAssignments(
        id: string,
        companyCodeIds: readonly string[],
        materialGroupCodes: readonly string[],
    ): Observable<void> {
        const url = this.env.getBase('purchasing-groups', id, 'assignments', 'bulk', 'by-code');
        const request: IBulkAssignPurchasingGroupAssignmentsRequestDto = {
            companyCodeIds,
            materialGroupCodes,
        };

        return this.postWithoutResponseBody(url.toString(), request, {
            customErrorhandling: true,
            pruneCacheWithPrefix: HttpCacheKeyPrefix.PURCHASING_GROUP,
        });
    }

    public bulkRemoveAssignments(id: string, assignmentIds: readonly string[]): Observable<void> {
        const url = this.env.getBase('purchasing-groups', id, 'assignments', 'bulk');
        const request: IBulkRemovePurchasingGroupAssignmentsRequestDto = {
            assignmentIds,
        };

        return this.deleteWithoutResponseBody(url.toString(), request, {
            customErrorhandling: true,
            pruneCacheWithPrefix: HttpCacheKeyPrefix.PURCHASING_GROUP,
        });
    }

    public assignMembers(id: string, customerIds: readonly string[]): Observable<void> {
        const url = this.env.getBase('purchasing-groups', id, 'members');
        const request: IPurchasingGroupMembersRequestDto = {
            customerIds,
        };

        return this.postWithoutResponseBody(url.toString(), request, {
            customErrorhandling: true,
            pruneCacheWithPrefix: HttpCacheKeyPrefix.PURCHASING_GROUP,
        });
    }

    public removeMembers(id: string, customerIds: readonly string[]): Observable<void> {
        const url = this.env.getBase('purchasing-groups', id, 'members');
        const request: IPurchasingGroupMembersRequestDto = {
            customerIds,
        };

        return this.deleteWithoutResponseBody(url.toString(), request, {
            customErrorhandling: true,
            pruneCacheWithPrefix: HttpCacheKeyPrefix.PURCHASING_GROUP,
        });
    }

    public isDuplicateCodeError(error: unknown): boolean {
        return error instanceof HttpErrorResponse && error.status === 409;
    }

    public isDeactivationConfirmationRequired(error: unknown): boolean {
        return (
            error instanceof HttpErrorResponse &&
            error.status === HttpStatusCode.Conflict &&
            this.getDeactivationActiveAssignmentCount(error) !== null
        );
    }

    public getDeactivationActiveAssignmentCount(error: unknown): number | null {
        if (!(error instanceof HttpErrorResponse) || !this.isObjectPayload(error.error)) {
            return null;
        }

        const payload = error.error as IPurchasingGroupErrorDto;
        return typeof payload.activeAssignmentCount === 'number' ? payload.activeAssignmentCount : null;
    }

    public isAssignmentConflict(error: unknown): boolean {
        return (
            error instanceof HttpErrorResponse &&
            error.status === HttpStatusCode.Conflict &&
            this.getAssignmentConflict(error) !== null
        );
    }

    public getAssignmentConflict(error: unknown): PurchasingGroupAssignmentConflict | null {
        if (!(error instanceof HttpErrorResponse) || error.status !== HttpStatusCode.Conflict) {
            return null;
        }

        const payload = this.coerceErrorPayloadObject(error.error);
        if (
            !payload ||
            typeof payload.assignedPurchasingGroupId !== 'string' ||
            typeof payload.assignedPurchasingGroupCode !== 'string' ||
            typeof payload.assignedPurchasingGroupName !== 'string' ||
            typeof payload.companyCodeId !== 'string' ||
            typeof payload.companyCode !== 'string' ||
            typeof payload.materialGroupId !== 'string' ||
            typeof payload.materialGroupCode !== 'string'
        ) {
            return null;
        }

        const conflict: IPurchasingGroupAssignmentConflictDto = {
            assignedPurchasingGroupId: payload.assignedPurchasingGroupId,
            assignedPurchasingGroupCode: payload.assignedPurchasingGroupCode,
            assignedPurchasingGroupName: payload.assignedPurchasingGroupName,
            companyCodeId: payload.companyCodeId,
            companyCode: payload.companyCode,
            materialGroupId: payload.materialGroupId,
            materialGroupCode: payload.materialGroupCode,
        };

        return new PurchasingGroupAssignmentConflict(conflict);
    }

    public getCompanyCodeRemovalBlocked(error: unknown): PurchasingGroupCompanyCodeRemovalBlocked | null {
        if (
            !(error instanceof HttpErrorResponse) ||
            error.status !== HttpStatusCode.Conflict ||
            !this.isObjectPayload(error.error)
        ) {
            return null;
        }

        const payload = error.error as unknown as IPurchasingGroupCompanyCodeRemovalBlockedDto;
        if (
            typeof payload.purchasingGroupId !== 'string' ||
            !Array.isArray(payload.blockingAssignments) ||
            payload.blockingAssignments.length === 0
        ) {
            return null;
        }

        return new PurchasingGroupCompanyCodeRemovalBlocked(payload);
    }

    public resolveErrorMessage(error: unknown, fallback: string): string {
        if (error instanceof HttpErrorResponse) {
            const payloadCode = this.extractErrorPayloadCode(error.error);
            const mappedCodeMessage = payloadCode ? this.mapKnownErrorCode(payloadCode) : null;
            if (mappedCodeMessage) {
                return mappedCodeMessage;
            }

            const payloadMessage = this.extractErrorPayloadMessage(error.error);
            const rawMessage = payloadMessage ?? error.message;

            if (rawMessage && !this.isTechnicalHttpErrorMessage(rawMessage)) {
                return rawMessage;
            }
        }

        return fallback;
    }

    private postWithoutResponseBody(url: string, body: unknown, params: ConstructorParameters<typeof HttpExtras>[0]): Observable<void> {
        return this.http
            .post(url, body, {
                observe: 'response',
                responseType: 'text',
                params: new HttpExtras(params),
            })
            .pipe(map(() => undefined));
    }

    private cacheParams(prefix: HttpCacheKeyPrefix): HttpExtras {
        return new HttpExtras({
            cacheKeyPrefix: prefix,
            customErrorhandling: true,
        });
    }

    private ignoreCacheParams(): HttpExtras {
        return new HttpExtras({
            ignoreCache: true,
            customErrorhandling: true,
        });
    }

    private pruneCacheParams(...prefix: HttpCacheKeyPrefix[]): HttpExtras {
        return new HttpExtras({
            pruneCacheWithPrefix: prefix,
            customErrorhandling: true,
        });
    }

    private deleteWithoutResponseBody(
        url: string,
        body: unknown,
        params: ConstructorParameters<typeof HttpExtras>[0],
    ): Observable<void> {
        return this.http
            .delete(url, {
                body,
                observe: 'response',
                responseType: 'text',
                params: new HttpExtras(params),
            })
            .pipe(map(() => undefined));
    }

    private isObjectPayload(payload: unknown): payload is Record<string, unknown> {
        return typeof payload === 'object' && payload !== null;
    }

    private coerceErrorPayloadObject(payload: unknown): Record<string, unknown> | null {
        if (typeof payload === 'string') {
            const parsedPayload = this.tryParseJsonPayload(payload);
            return this.isObjectPayload(parsedPayload) ? parsedPayload : null;
        }

        return this.isObjectPayload(payload) ? payload : null;
    }

    private extractErrorPayloadMessage(payload: unknown): string | null {
        if (typeof payload === 'string') {
            const parsedPayload = this.tryParseJsonPayload(payload);
            if (this.isObjectPayload(parsedPayload)) {
                return this.extractObjectMessage(parsedPayload) ?? payload;
            }

            return payload;
        }

        if (this.isObjectPayload(payload)) {
            return this.extractObjectMessage(payload);
        }

        return null;
    }

    private extractErrorPayloadCode(payload: unknown): PurchasingGroupErrorCode | null {
        if (typeof payload === 'string') {
            const parsedPayload = this.tryParseJsonPayload(payload);
            if (this.isObjectPayload(parsedPayload)) {
                return this.extractObjectErrorCode(parsedPayload);
            }

            return null;
        }

        if (this.isObjectPayload(payload)) {
            return this.extractObjectErrorCode(payload);
        }

        return null;
    }

    private extractObjectMessage(payload: Record<string, unknown>): string | null {
        if (typeof payload.message === 'string') {
            return payload.message;
        }

        if (typeof payload.error === 'string') {
            return payload.error;
        }

        return null;
    }

    private extractObjectErrorCode(payload: Record<string, unknown>): PurchasingGroupErrorCode | null {
        if (typeof payload.errorCode !== 'string') {
            return null;
        }

        return this.isPurchasingGroupErrorCode(payload.errorCode) ? payload.errorCode : null;
    }

    private tryParseJsonPayload(payload: string): unknown {
        try {
            return JSON.parse(payload);
        } catch {
            return null;
        }
    }

    private isTechnicalHttpErrorMessage(message: string): boolean {
        return (
            message.startsWith('Http failure response for ') ||
            message.startsWith('Http failure during parsing for ') ||
            message.startsWith('Unexpected token ') ||
            message.startsWith('Unexpected end of JSON input')
        );
    }

    private isPurchasingGroupErrorCode(value: string): value is PurchasingGroupErrorCode {
        return Object.values(PurchasingGroupErrorCode).includes(value as PurchasingGroupErrorCode);
    }

    private mapKnownErrorCode(errorCode: PurchasingGroupErrorCode): string | null {
        switch (errorCode) {
            case PurchasingGroupErrorCode.MATERIAL_GROUP_NOT_FOUND:
                return marker('Die ausgewählte Warengruppe wurde nicht gefunden.');
            case PurchasingGroupErrorCode.MATERIAL_GROUP_TENANT_MISMATCH:
                return marker('Die ausgewählte Warengruppe gehört nicht zum aktuellen Mandanten.');
            case PurchasingGroupErrorCode.INACTIVE_PURCHASING_GROUP_ASSIGNMENT:
                return marker('Neue Zuordnungen sind nur für aktive Einkäufergruppen möglich.');
            case PurchasingGroupErrorCode.INACTIVE_CUSTOMER_USERS_ASSIGNMENT:
                return marker('Es können nur aktive Benutzer als Mitglieder der Einkäufergruppe zugewiesen werden.');
            case PurchasingGroupErrorCode.COMPANY_CODE_NOT_FOUND:
                return marker('Der ausgewählte Buchungskreis wurde nicht gefunden.');
            case PurchasingGroupErrorCode.COMPANY_CODE_NOT_ASSIGNED_AT_HEADER:
                return marker('Der ausgewählte Buchungskreis ist dieser Einkäufergruppe nicht auf Kopfebene zugeordnet.');
            case PurchasingGroupErrorCode.BULK_ASSIGNMENT_LIMIT_EXCEEDED:
                return marker('Die Auswahl erzeugt zu viele Zuordnungen. Bitte prüfe die Auswahl.');
            case PurchasingGroupErrorCode.PURCHASING_GROUP_NOT_FOUND:
                return marker('Die Einkäufergruppe wurde nicht gefunden.');
            case PurchasingGroupErrorCode.PURCHASING_GROUP_ASSIGNMENT_CONFLICT:
                return marker('Eine oder mehrere Zuordnungen existieren bereits. Bitte zuerst entfernen.');
            default:
                return null;
        }
    }
}
